// Pure, DOM-free helpers for the "Add fields" catalog UX. Kept separate so the
// grouping + enable-state logic is unit-testable without a DOM.
import type { FieldType, FieldInstance } from '../api/types.js';

// Human-friendly labels + display order for catalog categories.
export const CATEGORY_LABELS: Record<string, string> = {
  name: 'Name',
  contact: 'Contact',
  address: 'Address',
  date: 'Date',
  media: 'Media',
  misc: 'Other',
  identifier: 'Identifiers',
  messaging: 'Messaging',
  social: 'Social',
  payment: 'Payment',
  loyalty: 'Loyalty & Memberships',
};

const CATEGORY_ORDER = ['name', 'contact', 'address', 'date', 'media', 'misc', 'identifier', 'messaging', 'social', 'payment', 'loyalty'];

export interface CatalogGroup { category: string; label: string; types: FieldType[]; }

// NAME_KEYS is the set of core "Name" section field keys that must stay
// single-instance (you only have one legal given/family/etc. name). Every OTHER
// field type — predefined or custom — may be added multiple times.
export const NAME_KEYS = new Set<string>([
  'given_name', 'family_name', 'middle_name', 'full_name', 'name', 'display_name', 'preferred_name',
]);

// isSingleInstanceType returns true ONLY for the core Name fields, which are the
// sole types locked to a single instance. Determined by the catalog category
// ('name') OR membership in NAME_KEYS (union of both, to be safe). Custom fields
// (no catalog entry) are never single-instance.
export function isSingleInstanceType(type: FieldType): boolean {
  return type.category === 'name' || NAME_KEYS.has(type.key);
}

// typeDisplayName maps a field TYPE key to a human-readable display name. Prefers
// the catalog label for the key, else falls back to the key uppercased. This is
// the PRIMARY label shown for a field (e.g. "PHONE"); the owner's per-field
// `label` is a separate optional qualifier/annotation (see qualifierSubline).
export function typeDisplayName(type: string | undefined, catalog?: FieldType[]): string {
  const key = (type || '').trim();
  if (!key) return '';
  if (catalog) {
    const hit = catalog.find((t) => t.key === key);
    if (hit && hit.label) return hit.label;
  }
  return key.toUpperCase();
}

// isCatalogType reports whether a field-type key corresponds to a known catalog
// entry. Custom fields (no catalog entry) reuse `label` as their DISPLAY NAME,
// whereas catalog fields reuse `label` as an optional QUALIFIER/annotation. The
// two must not be conflated, so views branch on this predicate.
export function isCatalogType(key: string | undefined, catalog?: FieldType[]): boolean {
  const k = (key || '').trim();
  if (!k || !catalog) return false;
  return catalog.some((t) => t.key === k);
}

// qualifierSubline decides whether/how to render a per-field qualifier annotation
// (the reused `label` column, e.g. "work"/"private") as a small muted sub-line
// UNDER the type name. Returns the parenthesized text ("(work)") when the label
// is non-empty after trimming, else null (render nothing).
export function qualifierSubline(label: string | undefined | null): string | null {
  const v = (label || '').trim();
  return v ? `(${v})` : null;
}

// profileFieldName resolves the PRIMARY display name for a stored field in the
// per-profile exposed-fields editor, matching My Contacts' fieldLabelBlock:
//  - CUSTOM fields (key startsWith `custom_`): their `label` IS the display name
//    (fall back to typeDisplayName when empty). The annotation subline is NOT
//    used — a custom field's label is not a qualifier.
//  - CATALOG fields: the friendly type name (typeDisplayName), with the optional
//    per-field `label` surfaced separately as a muted qualifier subline.
// Returns { name, subline, id } where `id` is the raw lowercase field id used
// ONLY as the native hover `title` (never the friendly name).
export interface ProfileFieldName { name: string; subline: string | null; id: string; }
export function profileFieldName(
  field: { field_type_key?: string; type?: string; label?: string; annotation?: string },
  catalog?: FieldType[],
): ProfileFieldName {
  const id = (field.field_type_key || field.type || '').trim();
  const isCustom = id.startsWith('custom_');
  if (isCustom) {
    const name = (field.label || '').trim() || typeDisplayName(id, catalog);
    // Custom fields put their display NAME in `label`; the optional qualifier
    // lives in the dedicated `annotation` slot.
    return { name, subline: qualifierSubline(field.annotation), id };
  }
  const name = typeDisplayName(id, catalog);
  return { name, subline: qualifierSubline(field.label), id };
}

// groupByCategory buckets catalog entries by category in a stable UX order.
// Unknown categories are appended alphabetically after the known ones.
export function groupByCategory(types: FieldType[]): CatalogGroup[] {
  const buckets = new Map<string, FieldType[]>();
  for (const t of types) {
    const cat = t.category || 'misc';
    (buckets.get(cat) || buckets.set(cat, []).get(cat)!).push(t);
  }
  const cats = Array.from(buckets.keys()).sort((a, b) => {
    const ia = CATEGORY_ORDER.indexOf(a), ib = CATEGORY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  return cats.map((category) => ({
    category,
    label: CATEGORY_LABELS[category] || category,
    types: buckets.get(category)!,
  }));
}

// fieldKey extracts the well-known type key from a stored field instance.
export function fieldKey(f: FieldInstance): string {
  return f.field_type_key || f.type || '';
}

export interface EnableState {
  // already-present count for this key.
  count: number;
  // true if the enable control should be disabled (single-value already added).
  disabled: boolean;
  // true if at least one instance already exists.
  added: boolean;
}

// enableState decides whether a catalog row can be enabled given the user's
// current fields. EVERY field type may be added again (predefined + custom),
// EXCEPT the core Name-section fields (isSingleInstanceType), which lock after
// the first instance. Custom fields (no catalog entry) are always multi.
export function enableState(type: FieldType, currentFields: FieldInstance[]): EnableState {
  const count = currentFields.filter((f) => fieldKey(f) === type.key).length;
  const added = count > 0;
  const disabled = added && isSingleInstanceType(type);
  return { count, disabled, added };
}

// slug normalizes a friendly name into a field-id slug: lowercase, trim, runs of
// non-alphanumerics collapse to a single underscore, and leading/trailing
// underscores are stripped. Used to derive custom_<slug> field ids.
export function slug(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

// ---- Field value validation (client-side mirror of the Go backend) ----
// The backend (internal/fieldcatalog.ValidateValue) is authoritative; these
// helpers give friendly inline UX and MUST stay in sync with its semantics.

const DATE_RE = /^\d{4}\/\d{2}\/\d{2}$/;
const SHORT_DATE_RE = /^\d{2}\/\d{2}$/;

// normalizeDateSeparators converts '-'/'.' to '/' to accept common variants.
export function normalizeDateSeparators(v: string): string {
  return v.replace(/[-.]/g, '/');
}

// isRealDate confirms a YYYY/MM/DD string is a real calendar date (round-trip).
function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

// validateFieldValue mirrors the server. Returns an error message or null (ok).
// Empty is accepted; unknown/absent format => no constraint.
export function validateFieldValue(fmt: string | undefined, value: string): string | null {
  const v = (value || '').trim();
  if (!v || !fmt) return null;
  if (fmt === 'date') {
    const n = normalizeDateSeparators(v);
    if (!DATE_RE.test(n)) return 'Date must be in YYYY/MM/DD format (e.g. 1990/07/05)';
    const [y, m, d] = n.split('/').map(Number);
    if (!isRealDate(y, m, d)) return 'That is not a real calendar date';
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (dt.getTime() > Date.now()) return 'Date of birth cannot be in the future';
    if (y < 1900) return 'Date of birth year is too far in the past (must be ≥ 1900)';
    return null;
  }
  if (fmt === 'short_date') {
    const n = normalizeDateSeparators(v);
    if (!SHORT_DATE_RE.test(n)) return 'Date must be in MM/DD format (e.g. 07/05)';
    const [m, d] = n.split('/').map(Number);
    // Use leap year 2000 so 02/29 is a valid birthday.
    if (!isRealDate(2000, m, d)) return 'That is not a real month/day';
    return null;
  }
  return null;
}

// canonicalizeFieldValue normalizes a valid value to its canonical stored form
// (slashes, zero-padded). Assumes validateFieldValue already returned null.
export function canonicalizeFieldValue(fmt: string | undefined, value: string): string {
  const v = (value || '').trim();
  if (!v || !fmt) return value;
  if (fmt === 'date' || fmt === 'short_date') return normalizeDateSeparators(v);
  return value;
}

// fieldInputType returns the best HTML input type for a format. Date formats use
// a native date picker; short_date has no year so it stays a text input.
export function fieldInputType(fmt: string | undefined): 'date' | 'text' {
  return fmt === 'date' ? 'date' : 'text';
}

// formatPlaceholder returns the human-readable input PATTERN to show as the
// value field's placeholder (gray hint) for a given validation format, so the
// user knows what to type. Single source of truth for these hints. Returns ''
// (no hint) for formats without a fixed pattern.
export function formatPlaceholder(fmt: string | undefined): string {
  switch (fmt) {
    case 'date': return 'YYYY/MM/DD';
    case 'short_date': return 'MM/DD';
    default: return '';
  }
}

// toDateInputValue converts a stored canonical YYYY/MM/DD into the native
// <input type="date"> value (YYYY-MM-DD). Returns '' if not a full date.
export function toDateInputValue(value: string): string {
  const n = normalizeDateSeparators((value || '').trim());
  return DATE_RE.test(n) ? n.replace(/\//g, '-') : '';
}

// fromDateInputValue converts a native date value (YYYY-MM-DD) back to canonical
// YYYY/MM/DD for the API. Non-date input passes through unchanged.
export function fromDateInputValue(value: string): string {
  const v = (value || '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.replace(/-/g, '/') : v;
}
